import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { initializeAxiosAuth, refreshAccessToken } from '@/lib/api'
import {
  clearAccessToken,
  getAccessToken,
  setAccessToken,
} from '@/lib/auth/token-storage'
import { AuthService, type LoginRequest, type User } from '@/services/auth.service'
import { registerWebPush, unregisterWebPush } from '@/services/firebase/web-push'
import { toast } from 'sonner'
import { getErrorMessage } from '@/lib/api-errors'

interface AuthContextType {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  token: string | null
  login: (data: LoginRequest) => Promise<{ success: boolean; message?: string }>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

/**
 * Storage strategy
 * ----------------
 *   - Access token  → in-memory + sessionStorage (see `lib/auth/token-storage`).
 *                     XSS exposure is bounded to a single tab's lifetime and
 *                     a 15-minute access-token TTL.
 *   - Refresh token → NEVER touched here. The backend ships it as an httpOnly
 *                     cookie; the browser attaches it to `/auth/refresh` and
 *                     `/auth/logout` automatically because axios is built with
 *                     `withCredentials: true`.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setTokenState] = useState<string | null>(() => getAccessToken())
  const [isLoading, setIsLoading] = useState(true)
  const queryClient = useQueryClient()

  // Keep a ref so the axios callback always sees the latest value without
  // having to be re-registered (which would race with in-flight requests).
  const tokenRef = useRef(token)
  tokenRef.current = token

  // Tracks whether `login()` has successfully established a session at any
  // point during this provider's lifetime. The mount-time `restoreSession`
  // below runs in parallel with whatever the user does in the UI — if
  // they click Login fast enough, login() can finish AND set state
  // (`setUser` + `setTokenState`) before our refresh-cookie / getMe
  // calls reject. Without this flag, the restore's catch arm then wipes
  // the freshly-set user back to null and the just-logged-in user is
  // silently bounced to the login screen. Pure ref so toggling it never
  // triggers a re-render.
  const loginEstablishedRef = useRef(false)

  // Initialize axios auth integration once. The callbacks here are stable
  // refs into AuthContext state; mutating `token` does NOT cause this effect
  // to re-run so requests in flight when login completes keep working.
  useEffect(() => {
    initializeAxiosAuth({
      getAccessToken: () => tokenRef.current,
      onTokenRefreshed: (newToken) => {
        setTokenState(newToken)
      },
      onAuthFailed: () => {
        setUser(null)
        setTokenState(null)
        clearAccessToken()
        toast.error('Session expired. Please log in again.')
      },
    })
    // initializeAxiosAuth is mounted-once; the callbacks read live state via
    // tokenRef and React's stable setters, so the dependency array is empty.
  }, [])

  // On mount: try the access token we already have; if that fails or there
  // is none, try refreshing via the httpOnly cookie. Either path lands the
  // user in an authenticated state without exposing the refresh token to JS.
  useEffect(() => {
    const restoreSession = async () => {
      const cachedToken = getAccessToken()
      try {
        if (cachedToken) {
          const userData = await AuthService.getMe()
          setUser(userData)
          setTokenState(cachedToken)
          // Re-register the FCM token on every cold-start. Server upsert is
          // idempotent and refreshes last_seen_at so the dispatcher can
          // prune stale entries without us doing it client-side.
          void registerWebPush()
          return
        }
        // No access token in memory — try the cookie path. If the cookie is
        // gone too the catch below cleans up to anon state. `silent: true`
        // keeps the "Session expired" toast from firing on every cold visit
        // — that toast is only for mid-session expiries (which 401-interceptor
        // path triggers).
        const newToken = await refreshAccessToken({ silent: true })
        setTokenState(newToken)
        const userData = await AuthService.getMe()
        setUser(userData)
        void registerWebPush()
      } catch {
        // Race guard — if a concurrent login() established the session
        // while our restore was still in flight, leave its state alone.
        // Without this the just-logged-in user would be silently
        // bounced back to the login screen.
        if (!loginEstablishedRef.current) {
          clearAccessToken()
          setTokenState(null)
          setUser(null)
        }
      } finally {
        setIsLoading(false)
      }
    }

    void restoreSession()
  }, [])

  const login = useCallback(async (data: LoginRequest) => {
    try {
      const response = await AuthService.login(data)
      const { token: authToken, user: userData } = response

      // ── Admin-only login gate ────────────────────────────────────────
      // The backend /auth/login endpoint is shared with the mobile app
      // and accepts every role. The admin web specifically refuses any
      // non-admin login here — we don't persist the token, we don't set
      // the user, and we return a clear error the login form can show.
      const normalizedRole = String(userData?.role ?? '').toLowerCase()
      const isAdmin =
        normalizedRole === 'admin' || normalizedRole === 'super_admin'
      if (!isAdmin) {
        // Tell the backend to clear the refresh cookie so a non-admin login
        // doesn't leave the browser holding a session it cannot use.
        try {
          await AuthService.logout()
        } catch {
          // best-effort
        }
        clearAccessToken()
        return {
          success: false,
          message:
            'This portal is restricted to platform administrators. Please use the mobile app.',
        }
      }

      // The response also carries `refreshToken` for mobile clients. We
      // deliberately ignore it — the same token is in the httpOnly cookie
      // the backend just set on this response, and that is the one we'll
      // use on refresh. Mirroring it into sessionStorage would re-introduce
      // the XSS exposure the cookie exists to remove.
      setAccessToken(authToken)
      setTokenState(authToken)

      const transformedUser: User = {
        id: userData.id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        phone: userData.phone,
        photoUrl: userData.photo_url,
        approved: userData.approved,
        permissions: userData.permissions ?? [],
      }

      setUser(transformedUser)
      // Mark the session as login-established BEFORE the in-flight
      // restoreSession's catch could run — if restore's await rejects
      // after this point, its catch will see the flag and skip the
      // state wipe that would otherwise log this user straight back out.
      loginEstablishedRef.current = true
      // Immediately rotate the refresh cookie via a dedicated
      // /auth/refresh roundtrip. Two reasons:
      //   1. Even though the /auth/login response already carries a
      //      Set-Cookie header, DevTools' Application → Cookies panel
      //      can stay stale until *something* on a path the cookie is
      //      scoped to (/api/auth) executes a second Set-Cookie. By
      //      kicking a refresh now, the cookie is observably present
      //      the moment the dashboard mounts — no hard-refresh-to-see-
      //      it footgun for QA / debug sessions.
      //   2. Token rotation on login is best practice — the new access
      //      token also lands here so the rest of the app starts on a
      //      freshly issued pair instead of the one that just shipped
      //      over the wire in the login response.
      // `silent: true` keeps a transient refresh failure from spawning a
      // misleading "Session expired" toast — the login has already
      // succeeded, so we don't tear the session down on a rotation hiccup.
      try {
        const rotatedToken = await refreshAccessToken({ silent: true })
        setTokenState(rotatedToken)
      } catch {
        // Rotation is opportunistic — if the backend hiccups, the user
        // is still logged in with the original token pair from the
        // /auth/login response and can proceed normally.
      }
      // Register this browser tab for FCM web push. Fire-and-forget so a
      // missing Firebase config or denied permission doesn't block login.
      void registerWebPush()
      return { success: true }
    } catch (error) {
      return { success: false, message: getErrorMessage(error, 'Login failed') }
    }
  }, [])

  const refreshUser = useCallback(async () => {
    try {
      const userData = await AuthService.getMe()
      setUser(userData)
    } catch {
      // Ignore — user will remain as-is
    }
  }, [])

  const logout = useCallback(async () => {
    // Revoke the FCM token BEFORE clearing the access token — the
    // /notifications/devices/:token DELETE needs auth to identify the
    // owner, and clearAccessToken() below strips the bearer.
    try {
      await unregisterWebPush()
    } catch {
      // best-effort; logout proceeds regardless
    }
    try {
      // Backend clears the httpOnly refresh cookie; idempotent (returns 204
      // even if no cookie is present). We still wipe local state if it errors.
      // The `await` here is load-bearing — it guarantees the Set-Cookie
      // header that clears `protea_rt` has been fully processed by the
      // browser BEFORE we tear down React state below. Without it, the
      // logout state flip could finish before the cookie deletion lands,
      // which is the timing window the user observed as "cookie not
      // cleared until hard refresh".
      await AuthService.logout()
    } catch {
      // Ignore logout API errors
    } finally {
      setUser(null)
      setTokenState(null)
      clearAccessToken()
      // Reset the race-guard so a subsequent restore-on-reload (after the
      // user logs back in within the same session) can again rely on this
      // flag rather than seeing a stale value.
      loginEstablishedRef.current = false
      queryClient.clear()
    }
  }, [queryClient])

  // Memoise the context value so consumers don't re-render on every parent
  // update; only the four state slices below should drive consumer renders.
  const value = useMemo<AuthContextType>(
    () => ({
      user,
      isAuthenticated: !!user && !!token,
      isLoading,
      token,
      login,
      logout,
      refreshUser,
    }),
    [user, token, isLoading, login, logout, refreshUser],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
